import { all, fork, put, takeEvery, takeLatest } from 'typed-redux-saga/macro';
import { get, isString } from 'lodash';
import {
  checkFullUploadDone,
  hideUploadForm,
  storeFileUploadFailure,
  storeFileUploading,
  storeFileUploadSuccess,
  UPLOAD_FILE_FAILURE,
  UPLOAD_FILE_SUCCESS,
  UPLOAD_SUBMIT,
  uploadFile,
} from './index';

import { UPLOAD_COMPLETE } from '@common-modules/filePicker';
import { actionCreateTDO, CREATE_TDO_SUCCESS } from '../uploads';
import { STAGE_UPLOAD_LEXICON_BATCH } from '../documents/actions';
import { UploadResult } from '@common/state/modules/filePicker/saga';
import {
  DOCUMENT_REDACTION_EXTENSIONS,
  DOCUMENT_REDACTION_MIME_TYPES,
} from '@helpers/constants';

export function* uploadMedia() {
  yield* takeEvery(UPLOAD_SUBMIT, function* (action) {
    const { file, key } = action.payload;
    yield* put(storeFileUploading(key));
    yield* put(uploadFile(file, key));
  });
}

export function* uploadFileTracker() {
  yield* takeEvery(UPLOAD_FILE_SUCCESS, function* (action) {
    yield* put(storeFileUploadSuccess(action.meta.variables.key));
    // TODO: fix this
    yield* put(checkFullUploadDone() as any);
  });

  yield* takeEvery(UPLOAD_FILE_FAILURE, function* (action) {
    yield* put(storeFileUploadFailure(action.meta.variables.key));
    yield* put(checkFullUploadDone() as any);
  });
}

function hasGetUrl<T extends UploadResult>(
  ur: T
): ur is T & { getUrl: string } {
  return isString(ur.getUrl);
}

export function* handleUploadComplete(
  action: ReturnType<typeof UPLOAD_COMPLETE>
) {
  // create media tdo after upload success
  const files = action.payload;
  const {
    govQARequestId,
    foiaXpressRequestId,
    casepointRequestId,
    nuixRequestId,
    exterroRequestId,
    caseId,
    settingsProfileId,
  } = action.meta || {};
  const filteredFiles = files.filter((file) => {
    const mimeType = get(file, 'type', '').toString();
    const fileType = mimeType.split('/')[0];
    const fileName = get(file, 'fileName', '').toString().toLowerCase();
    const fileExt = fileName.substring(fileName.lastIndexOf('.'));
    return (
      fileType === 'video' ||
      fileType === 'audio' ||
      fileType === 'image' ||
      (DOCUMENT_REDACTION_MIME_TYPES as readonly string[]).includes(mimeType) ||
      (DOCUMENT_REDACTION_EXTENSIONS as readonly string[]).includes(fileExt) ||
      mimeType === 'binary/octet-stream' ||
      mimeType === 'application/x-shockwave-flash' // Note: SWF File Mimetype
    );
  });

  // Custom lexicons (VE-24948 / U5, `U5-FR14`): snapshot the picker's lexicon
  // selection against THIS batch's files before any TDO exists, so every PDF
  // of a multi-file upload gets the selection recorded at CREATE_TDO_SUCCESS —
  // and nothing created outside the batch can inherit it.
  const uploadUrls = filteredFiles.filter(hasGetUrl).map((file) => file.getUrl);
  yield* put(STAGE_UPLOAD_LEXICON_BATCH({ fileUrls: uploadUrls }));

  for (const file of filteredFiles) {
    if (hasGetUrl(file)) {
      yield* put(
        actionCreateTDO(
          file,
          govQARequestId,
          foiaXpressRequestId,
          casepointRequestId,
          nuixRequestId,
          exterroRequestId,
          undefined,
          caseId,
          settingsProfileId
        )
      );
    }
  }
}

export function* handleFilePicker() {
  yield* takeLatest(UPLOAD_COMPLETE, handleUploadComplete);

  yield* takeLatest(CREATE_TDO_SUCCESS, function* () {
    yield* put(hideUploadForm());
  });
}

export default function* overviewSaga() {
  yield* all([fork(handleFilePicker)]);
}
